/**
 * Practice location -> EHR vendor.
 *
 * The backend already knows this (ehr_practicelocation_credentials.ehr_vendor),
 * but the portal needs it before the call is made: eCW and Athena expose
 * different sync routes, so the vendor decides which URL to hit. Keeping it
 * here avoids a lookup round-trip on every screen.
 *
 * Add a row whenever a practice location is provisioned for an EHR.
 */

export enum EhrVendor {
  ECW = "ECW",
  ATHENA = "ATHENA",
}

/**
 * practiceLocationId -> vendor. Keys must match
 * redox_patient.pratice_location_id.
 */
export const PracticeEhrVendorMap: Record<number, EhrVendor> = {
  1000: EhrVendor.ECW,
  1003: EhrVendor.ATHENA,
};

/**
 * Returns the vendor for a practice location, or null when it is not
 * EHR-enabled.
 */
export const getEhrVendor: (
  practiceLocationId: number | null | undefined,
) => EhrVendor | null = (practiceLocationId) => {
  if (practiceLocationId === null || practiceLocationId === undefined) {
    return null;
  }
  return PracticeEhrVendorMap[practiceLocationId] ?? null;
};

/**
 * True when the practice is wired to an EHR, i.e. whether the sync buttons
 * should be shown at all.
 */
export const isEhrEnabled: (
  practiceLocationId: number | null | undefined,
) => boolean = (practiceLocationId) =>
  getEhrVendor(practiceLocationId) !== null;

/** Display label for the sync button tooltip. */
export const getEhrVendorLabel: (
  practiceLocationId: number | null | undefined,
) => string = (practiceLocationId) => {
  const vendor = getEhrVendor(practiceLocationId);
  if (vendor === EhrVendor.ECW) return "eCW";
  if (vendor === EhrVendor.ATHENA) return "athenahealth";
  return "";
};

/**
 * Display name for the vendor string the capabilities response returns.
 *
 * Driven by the API's own value rather than {@link PracticeEhrVendorMap}, so a
 * practice that is not in that hardcoded table still gets a real name. Falls
 * back to whatever the server said, which is better than showing "EHR".
 */
export const ehrVendorDisplayName: (
  vendor: string | null | undefined,
) => string = (vendor) => {
  const key = String(vendor ?? "").toUpperCase();
  if (key === EhrVendor.ECW) return "eCW";
  if (key === EhrVendor.ATHENA) return "athenahealth";
  return String(vendor ?? "");
};
